import { Box, Grid, Stack } from '@mui/material'
import ReturnButton from 'components/Button/ReturnButton'
import LoaderSpinner from 'components/LoaderSpinner/LoaderSpinner'
import Title from 'components/Title/Title'
import { Polygon } from 'geojson'
import useCreateCleaningZone from 'hooks/mission/useCreateCleaningZone'
import useCreateRestrictZone from 'hooks/mission/useCreateRestrictZone'
import useEditCleaningZone from 'hooks/mission/useEditCleaningZone'
import useEditRestrictZone from 'hooks/mission/useEditRestrictZone'
import useMission from 'hooks/useMission'
import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import useMissionStore from 'store/mission'
import {
  MISSION_CALCULATING_STATUSES,
  MISSION_PAGE_MODES,
  MISSION_ZONE_TYPES,
  PATH,
  PROGRESS_TYPES,
  TIMEOUT_FETCHING_STATUS,
} from 'utils/constants'
import { IZoneDetailsData } from 'utils/interfaces'
import { useShallow } from 'zustand/react/shallow'

import MissionButtons from './MissionButtons/MissionButtons'
import MissionSettingsModal from './MissionButtons/MissionSettingsModal/MissionSettingsModal'
import MissionMap from './MissionMap/MissionMap'
import ViewCreateZones from './Modes/ViewZone/ViewCreateZones'
import ZoneDetailsView from './Modes/ZoneDetails/ZoneDetailsView'

const MissionView = () => {
  const navigate = useNavigate()
  const {
    mode,
    calculatingStatus,
    editingZone,
    tempZone,
    tempStartingPoint,
    tempEndingPoint,
    missionInfo,
    setEditingZone,
    setTempZone,
    setSavingZoneProgress,
    createCleaningZone,
    createRestrictZone,
    editChangeCleaningZone,
    setChangeRestrictZone,
    fetchCalculationStatus,
  } = useMissionStore(
    useShallow(state => ({
      mode: state.mode,
      calculatingStatus: state.calculatingStatus,
      editingZone: state.editingZone,
      tempZone: state.tempZone,
      tempStartingPoint: state.tempStartingPoint,
      tempEndingPoint: state.tempEndingPoint,
      missionInfo: state.missionInfo,
      setEditingZone: state.setEditingZone,
      setTempZone: state.setTempZone,
      setSavingZoneProgress: state.setSavingZoneProgress,
      createCleaningZone: state.createCleaningZone,
      createRestrictZone: state.createRestrictZone,
      editChangeCleaningZone: state.editChangeCleaningZone,
      setChangeRestrictZone: state.setChangeRestrictZone,
      fetchCalculationStatus: state.fetchCalculationStatus,
    }))
  )

  const { data: missionInfoData } = useMission()
  const { mutateAsync: setCleaningZone, isPending: isCreateCleaningPending } = useCreateCleaningZone()
  const { mutateAsync: setRestrictZone, isPending: isCreateRestrictPending } = useCreateRestrictZone()
  const { mutateAsync: setEditCleaningZone, isPending: isEditCleaningPending } = useEditCleaningZone()
  const { mutateAsync: setEditRestrictZone, isPending: isEditRestrictPending } = useEditRestrictZone()

  useEffect(() => {
    let counter = 0
    const interval = setInterval(async () => {
      if (missionInfoData) {
        try {
          fetchCalculationStatus(missionInfoData.mission?.id, counter % 2 !== 0)
          counter += 1
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log('Unable to get mission status')
        }
      }
    }, TIMEOUT_FETCHING_STATUS)
    return () => {
      clearInterval(interval)
    }
  }, [])

  const onSaveZoneClicked = async (data: IZoneDetailsData) => {
    if (!tempZone) {
      return
    }
    if (data.zoneType === MISSION_ZONE_TYPES.CLEANING) {
      const startingPoint = tempStartingPoint?.geometry
      const endingPoint = tempEndingPoint?.geometry
      if (endingPoint && !startingPoint) {
        return
      }
      setCleaningZone({
        missionId: missionInfoData?.mission.id || -1,
        name: data.name,
        description: data.description,
        geometry: tempZone.geometry as Polygon,
        startingPoint,
        endingPoint,
        cleaningConfig: data.cleaningConfig,
      })
        .then(result => {
          createCleaningZone(result.zone, result.startingPoint, result.endingPoint)
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.log('Error creating cleaning zone', error)
          setTempZone(undefined)
          setSavingZoneProgress(PROGRESS_TYPES.ERROR)
        })
    } else if (data.zoneType === MISSION_ZONE_TYPES.RESTRICT) {
      setRestrictZone({
        missionId: missionInfoData?.mission.id || -1,
        name: data.name,
        description: data.description,
        geometry: tempZone.geometry as Polygon,
      })
        .then(result => {
          createRestrictZone(result)
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.log('Error creating restrict zone', error)
          setTempZone(undefined)
          setSavingZoneProgress(PROGRESS_TYPES.ERROR)
        })
    }
  }

  const onSaveChangedZoneClicked = async (data: IZoneDetailsData) => {
    if (!tempZone) {
      return
    }
    if (data.zoneType === MISSION_ZONE_TYPES.CLEANING) {
      const startingPoint = tempStartingPoint?.geometry
      const endingPoint = tempEndingPoint?.geometry
      if (endingPoint && !startingPoint) {
        return
      }
      setEditCleaningZone({
        missionId: missionInfoData?.mission.id || -1,
        name: data.name,
        zoneId: tempZone.properties?.zone_id,
        description: data.description,
        geometry: tempZone.geometry as Polygon,
        startingPoint,
        endingPoint,
        cleaningConfig: data.cleaningConfig,
      })
        .then(result => {
          editChangeCleaningZone(
            tempZone?.properties?.zone_id || -1,
            result.zone,
            result.startingPoint,
            result.endingPoint
          )
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.log('Error editing cleaning zone', error)
          setEditingZone(undefined)
          setSavingZoneProgress(PROGRESS_TYPES.ERROR)
        })
    } else {
      setEditRestrictZone({
        missionId: missionInfoData?.mission.id || -1,
        name: data.name,
        zoneId: tempZone.properties?.zone_id,
        description: data.description,
        geometry: tempZone.geometry as Polygon,
      })
        .then(result => {
          setChangeRestrictZone(result)
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.log('Error editing restrict zone', error)
          setEditingZone(undefined)
        })
    }
  }

  const isCalculating = calculatingStatus === MISSION_CALCULATING_STATUSES.CALCULATING

  const modeList: Partial<Record<MISSION_PAGE_MODES, JSX.Element>> = useMemo(
    () => ({
      [MISSION_PAGE_MODES.DRAW_ZONE]: (
        <ZoneDetailsView onSave={onSaveZoneClicked} isPending={isCreateCleaningPending || isCreateRestrictPending} />
      ),
      [MISSION_PAGE_MODES.VIEW_ZONES]: <ViewCreateZones />,
      [MISSION_PAGE_MODES.EDIT_ZONE]: (
        <ZoneDetailsView
          onSave={onSaveChangedZoneClicked}
          zone={editingZone}
          isPending={isEditCleaningPending || isEditRestrictPending}
        />
      ),
      [MISSION_PAGE_MODES.VIEW_SETTINGS]: <MissionSettingsModal missionId={missionInfo!.id} />,
    }),
    [
      editingZone,
      missionInfo,
      onSaveZoneClicked,
      onSaveChangedZoneClicked,
      isCreateCleaningPending,
      isCreateRestrictPending,
      isEditCleaningPending,
      isEditRestrictPending,
    ]
  )

  return (
    <>
      <Grid container height="100%" width="100%">
        <Grid xs={12} md={6} item paddingY={2} height="100%">
          <Box height="100%" maxHeight="100%" paddingRight={{ xs: 0, md: 2 }} display="flex" flexDirection="column">
            <Box width="100%" height="100%">
              <Stack direction="row" alignItems="center" gap={2} height="10%" minHeight="60px">
                <ReturnButton
                  onClick={() => {
                    navigate(PATH.MISSION_LIST)
                  }}
                />
                <>
                  <Stack gap={2} direction="row">
                    <Title text={missionInfo?.name} />
                    <Title text={'ID:' + missionInfo?.id} />
                  </Stack>
                </>
              </Stack>
              <Stack spacing={2} height="90%">
                <Box>
                  <MissionButtons />
                </Box>

                <Box position="relative" bgcolor="info.main" borderRadius={2} height="100%" overflow="auto">
                  {isCalculating && (
                    <Box
                      position="absolute"
                      top={0}
                      left={0}
                      right={0}
                      bottom={0}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      zIndex={10}
                      sx={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}
                    >
                      <LoaderSpinner />
                    </Box>
                  )}
                  {modeList[mode]}
                </Box>
              </Stack>
            </Box>
          </Box>
        </Grid>
        <Grid xs={12} md={6} item height="100%" paddingY={2} paddingTop={{ xs: 3, sm: 3, lg: 2 }}>
          <Box width="100%" height="100%" borderRadius={2} overflow="hidden">
            <MissionMap />
          </Box>
        </Grid>
      </Grid>
    </>
  )
}

export default MissionView
